import { DataGrid } from '@mui/x-data-grid';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminList.scss';
import BASE_URL from '../../../config/apiConfig';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField } from '@mui/material';

function AdminList() {
    const [data, setData] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [isViewOpen, setIsViewOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [adminToDelete, setAdminToDelete] = useState(null);
    const [adminToView, setAdminToView] = useState(null);
    const [adminToEdit, setAdminToEdit] = useState(null);
    const [newAdmin, setNewAdmin] = useState({
        username: '',
        email: '',
        password: '',
    });

    // Fetch admin user data from API on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const userResponse = await axios.get(`${BASE_URL}/api/admin/admin-users`);
                const transformedData = userResponse.data.map((admin) => ({
                    id: admin.id,
                    username: admin.username,
                    email: admin.email,
                    createdAt: admin.created_at ? new Date(admin.created_at).toLocaleDateString() : 'N/A',
                }));
                setData(transformedData);
            } catch (error) {
                console.error('Error fetching admin users:', error);
            }
        };

        fetchData();
    }, []);

    const handleDlt = (admin) => {
        setAdminToDelete(admin);
        setIsDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axios.delete(`${BASE_URL}/api/admin/deleteAdmin/${adminToDelete.id}`);
            setData((prevData) => prevData.filter((item) => item.id !== adminToDelete.id));
            setIsDeleteConfirmOpen(false);
            setAdminToDelete(null);
        } catch (error) {
            console.error('Error deleting admin:', error);
        }
    };

    const handleView = (admin) => {
        setAdminToView(admin);
        setIsViewOpen(true);
    };

    const handleEdit = (admin) => {
        setAdminToEdit({ ...admin }); // Create a copy of the admin object to edit
        setIsEditOpen(true);
    };

    const handleInputChange = (e) => {
        setNewAdmin({ ...newAdmin, [e.target.name]: e.target.value });
    };

    const handleAddNew = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${BASE_URL}/api/admin/create`, newAdmin);
            const addedAdmin = {
                id: response.data.admin.id,
                username: response.data.admin.username,
                email: response.data.admin.email,
                createdAt: new Date().toLocaleDateString(),
            };
            setData((prevData) => [...prevData, addedAdmin]);
            setIsModalOpen(false);
            setNewAdmin({ username: '', email: '', password: '' });
        } catch (error) {
            console.error('Error adding new admin:', error);
        }
    };

    const handleUpdateAdmin = async (e) => {
        e.preventDefault();
        try {
            // Use the new API endpoint for updating admin details
            const response = await axios.put(
              `${BASE_URL}/api/admin/updateAdmin/${adminToEdit.id}`,
              adminToEdit
            );
            const updatedAdmin = {
                id: response.data.admin.id,
                username: response.data.admin.username,
                email: response.data.admin.email,
                createdAt: adminToEdit.createdAt,
            };
            setData((prevData) => prevData.map((item) => (item.id === updatedAdmin.id ? updatedAdmin : item)));
            setIsEditOpen(false);
            setAdminToEdit(null);
        } catch (error) {
            console.error('Error updating admin:', error);
        }
    };

    const columns = [
        { field: 'id', headerName: 'ID', width: 200 ,
        renderCell: (param) => (
            <div className="userr">
              <img
                src="/Images/defaultProfileDp.png"
                alt="User"
                className="userr_image"
              />
              {param.row.id}
            </div>
          ),
        },
        { field: 'username', headerName: 'Username', width: 180 },
        { field: 'email', headerName: 'Email', width: 280 },
        { field: 'createdAt', headerName: 'Created At', width: 180 },
        {
            field: 'action',
            headerName: 'Action',
            width: 300,
            renderCell: (params) => (
                <div className="action-buttons">
                    <button type="button" className="view-button" onClick={() => handleView(params.row)}>
                        View
                    </button>
                    <button type="button" className="edit-button" onClick={() => handleEdit(params.row)}>
                        Edit
                    </button>
                    <button type="button" className="delete-button" onClick={() => handleDlt(params.row)}>
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="btnn">
                <button type="button" onClick={() => setIsModalOpen(true)}>Add New</button>
            </div>

            <div className="data-table">
                <DataGrid
                    className="data-grid"
                    rows={data}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10]}
                    checkboxSelection
                    getRowId={(row) => row.id}
                />
            </div>

            {/* MUI Dialog for Add New Admin */}
            <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <DialogTitle>Add New Admin</DialogTitle>
                <form onSubmit={handleAddNew}>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            label="Username"
                            type="text"
                            fullWidth
                            name="username"
                            value={newAdmin.username}
                            onChange={handleInputChange}
                            required
                        />
                        <TextField
                            margin="dense"
                            label="Email"
                            type="email"
                            fullWidth
                            name="email"
                            value={newAdmin.email}
                            onChange={handleInputChange}
                            required
                        />
                        <TextField
                            margin="dense"
                            label="Password"
                            type="password"
                            fullWidth
                            name="password"
                            value={newAdmin.password}
                            onChange={handleInputChange}
                            required
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsModalOpen(false)} color="secondary">
                            Cancel
                        </Button>
                        <Button type="submit" color="primary">
                            Save
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* MUI Dialog for Delete Confirmation */}
            <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <p>Are you sure you want to delete the following admin?</p>
                    <p><strong>Username:</strong> {adminToDelete?.username}</p>
                    <p><strong>Email:</strong> {adminToDelete?.email}</p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDeleteConfirmOpen(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="primary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* MUI Dialog for Viewing Admin Details */}
            <Dialog open={isViewOpen} onClose={() => setIsViewOpen(false)}>
                <DialogTitle>Admin Details</DialogTitle>
                <DialogContent>
                    {adminToView && (
                        <div>
                            <p><strong>Username:</strong> {adminToView.username}</p>
                            <p><strong>Email:</strong> {adminToView.email}</p>
                            <p><strong>Created At:</strong> {adminToView.createdAt}</p>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsViewOpen(false)} color="secondary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* MUI Dialog for Editing Admin Details */}
            <Dialog open={isEditOpen} onClose={() => setIsEditOpen(false)}>
                <DialogTitle>Edit Admin</DialogTitle>
                <form onSubmit={handleUpdateAdmin}>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            label="Username"
                            type="text"
                            fullWidth
                            name="username"
                            value={adminToEdit?.username || ''}
                            onChange={(e) => setAdminToEdit({ ...adminToEdit, username: e.target.value })}
                            required
                        />
                        <TextField
                            margin="dense"
                            label="Email"
                            type="email"
                            fullWidth
                            name="email"
                            value={adminToEdit?.email || ''}
                            onChange={(e) => setAdminToEdit({ ...adminToEdit, email: e.target.value })}
                            required
                        />
                        <TextField
                            margin="dense"
                            label="Password"
                            type="password"
                            fullWidth
                            name="password"
                            onChange={(e) => setAdminToEdit({ ...adminToEdit, password: e.target.value })}
                            placeholder="Leave blank to keep current password"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setIsEditOpen(false)} color="secondary">
                            Cancel
                        </Button>
                        <Button type="submit" color="primary">
                            Save
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}

export default AdminList;
